import React, { useEffect, useState } from 'react';
import { Eye, Timer } from '@phosphor-icons/react';

// Stable pseudo-random "live viewers" count from product id (fake social proof)
export const fakeViewers = (id = '') => {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = (h * 31 + String(id).charCodeAt(i)) >>> 0;
  const base = 6 + (h % 22); // 6..27
  const wobble = (new Date().getMinutes() + h) % 5; // slight movement
  return base + wobble;
};

// Discount info from a product (percent + euro amount saved)
export const discountInfo = (p) => {
  const price = parseFloat(p?.price);
  const sale = parseFloat(p?.sale_price);
  if (!price || !sale || sale >= price) return null;
  return {
    percent: Math.round((1 - sale / price) * 100),
    amount: (price - sale).toFixed(2).replace('.', ','),
  };
};

// Resolve product brand (category with a brand logo) → { name, logo }
export const getProductBrand = (p, categories = []) => {
  if (!categories?.length || !p?.category_ids?.length) return null;
  const byId = Object.fromEntries(categories.map(c => [c.id, c]));
  for (const cid of p.category_ids) {
    const c = byId[cid];
    if (c && (c.brand_logo || c.parent_id)) {
      if (c.brand_logo) return { name: c.name, logo: c.brand_logo };
    }
  }
  return null;
};

// Resolve a display category name (prefer a product-type category, not a brand)
export const getProductCategoryName = (p, categories = []) => {
  if (!categories?.length || !p?.category_ids?.length) return p?.category || '';
  const byId = Object.fromEntries(categories.map(c => [c.id, c]));
  const cats = p.category_ids.map(id => byId[id]).filter(Boolean);
  const type = cats.find(c => !c.brand_logo && /lunette|lentille|solaire|vue|connect/i.test(c.name || ''));
  return (type || cats.find(c => !c.brand_logo) || cats[0])?.name || '';
};

// Sale end date for a product (explicit, else end of today for promos → urgency)
export const saleEndDate = (p) => {
  if (p?.date_on_sale_to) {
    const d = new Date(p.date_on_sale_to);
    if (!isNaN(d) && d > new Date()) return d;
  }
  if (p?.is_promo && p?.sale_price) {
    const end = new Date(); end.setHours(23, 59, 59, 0);
    return end;
  }
  return null;
};

const pad = (n) => String(n).padStart(2, '0');

// Live countdown chip
export const Countdown = ({ end, accent = '#FF004D', compact = false }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!end) return null;
  const diff = Math.max(0, new Date(end).getTime() - now);
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const label = days > 0 ? `${days}j ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
  return (
    <span className={`inline-flex items-center gap-1.5 ${compact ? 'text-[11px] px-2 py-1' : 'text-[12px] px-2.5 py-1.5'} font-semibold rounded-lg`}
      style={{ backgroundColor: accent + '14', color: accent }} data-testid="offer-countdown">
      <Timer size={compact ? 12 : 14} weight="fill" />
      <span>Offre&nbsp;: {label}</span>
    </span>
  );
};

// Live viewers chip
export const LiveViewers = ({ id, compact = false }) => {
  const n = fakeViewers(id);
  return (
    <span className={`inline-flex items-center gap-1.5 ${compact ? 'text-[10.5px]' : 'text-[12px]'} text-slate-500`} data-testid="live-viewers">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      {n} personnes consultent ce produit
    </span>
  );
};
